/**
 * SignupHubPage
 * ─────────────
 * Single landing page that lists every <BusinessSignup> manifest variant.
 * Used for the investor demo to show "one form, ten flows, one registry".
 *
 * Each card links to /signup/:manifestId.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import { MANIFESTS } from "@/lib/businessSignupManifests";

const MODULE_BADGE = {
  plaza:    { label: "Plaza",    accent: "bg-amber-100 text-amber-900 border-amber-300" },
  plate:    { label: "Plate",    accent: "bg-stone-900 text-amber-200 border-stone-900" },
  plugin:   { label: "Plugin",   accent: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  property: { label: "Property", accent: "bg-indigo-100 text-indigo-900 border-indigo-300" },
  deals:    { label: "Deals",    accent: "bg-rose-100 text-rose-900 border-rose-300" },
  chat:     { label: "Chat",     accent: "bg-sky-100 text-sky-900 border-sky-300" },
  music:    { label: "Music",    accent: "bg-violet-100 text-violet-900 border-violet-300" },
};

export default function SignupHubPage() {
  const manifests = Object.values(MANIFESTS);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="signup-hub">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700">
            HustlePlaza · unified signup
          </p>
          <h1 className="text-3xl font-bold text-stone-900 mt-2 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-stone-600" />
            One form. Every module.
          </h1>
          <p className="text-[14px] text-stone-600 mt-2 max-w-2xl">
            Every signup across HustlePlaza flows through a single
            manifest-driven form. ABN gets verified live against the
            ABR, the directory is upserted in <code className="text-stone-800">hc_directory_au</code>,
            and the central entity registry is linked across modules.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid gap-4 sm:grid-cols-2">
          {manifests.map((m) => {
            const badge = MODULE_BADGE[m.module] || { label: m.module, accent: "bg-stone-100 text-stone-900 border-stone-300" };
            return (
              <Link
                key={m.id}
                to={`/signup/${m.id}`}
                data-testid={`signup-card-${m.id}`}
                className="group bg-white border border-stone-200 hover:border-stone-900 rounded-xl p-5 transition-all duration-200 hover:shadow-md flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${badge.accent}`}>
                    {badge.label}
                    {m.country ? ` · ${m.country.toUpperCase()}` : ""}
                    {m.international ? " · intl" : ""}
                  </span>
                  <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-900 group-hover:translate-x-0.5 transition-all" />
                </div>
                <h2 className="text-[16px] font-bold text-stone-900">{m.title}</h2>
                {m.subtitle && (
                  <p className="text-[13px] text-stone-600 mt-1">{m.subtitle}</p>
                )}
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-3 text-[11px] text-stone-500">
                  <span>{m.sections.length} sections</span>
                  <span>·</span>
                  <span>{m.business_id_required ? "ABN required" : "ABN optional"}</span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 rounded-xl border border-stone-200 bg-white p-5 text-[13px] text-stone-700">
          <p className="font-bold text-stone-900 mb-1">How it works under the hood</p>
          <ol className="space-y-1 list-decimal pl-5">
            <li>Form posts to <code className="text-stone-800">/api/business-signup/{`{manifest_id}`}</code></li>
            <li>Server upserts <code className="text-stone-800">hc_directory_{`{country}`}.directory</code></li>
            <li>Server inserts module record (e.g. <code className="text-stone-800">hc_plaza.signups</code>)</li>
            <li>Server links the ABN in the central <code className="text-stone-800">hc_entities</code> graph</li>
          </ol>
        </div>
      </main>
    </div>
  );
}
